import { ComponentRef, useRef, useState } from 'react';

import { HighScoreEntity } from '~/interfaces';
import { Autocomplete } from '~/modules/elements/autocomplete';
import useRecentPlayerNames from '~/modules/hooks/players/use-recent-player-names';
import useKeyboardNav from '~/modules/hooks/use-keyboard-nav';

interface Props {
  index: number;
  singSetupId: string;
  score: HighScoreEntity;
  register: ReturnType<typeof useKeyboardNav>['register'];
  onSave: (singId: string, score: number, oldName: string, newName: string) => void;
}

function HighScoreRename({ score, register, singSetupId, onSave, index }: Props) {
  const inputRef = useRef<ComponentRef<typeof Autocomplete>>(null);
  const [newName, setNewName] = useState('');
  const playerNames = useRecentPlayerNames();

  const save = (name: string) => {
    // Save the trimmed value, not the raw one — it's what the check above validates against.
    const trimmed = name.trim();
    if (trimmed.length && trimmed !== score.name) onSave(singSetupId, score.score, score.name, trimmed);
  };

  const onBlur = () => save(newName);

  const onActive = () => {
    inputRef.current?.element?.focus();
  };

  // Screen-only. Correcting a fallback name is a keyboard job on the television, and on a phone it is
  // nobody's job at all - there is nothing to ask somebody who is already a player. `hideOnRemote`
  // rather than no registration, so the screen keeps full coverage and High scores goes on mirroring.
  return (
    <Autocomplete
      className="ph-no-capture [&_input]:lg:text-md [&_button]:h-8 [&_button]:lg:h-10 [&_input]:text-sm"
      options={playerNames}
      onChange={setNewName}
      onBlur={onBlur}
      value={newName}
      label=""
      ref={inputRef}
      {...register(`highscore-rename-${index}`, onActive, undefined, false, { hideOnRemote: true })}
      placeholder={score.name}
      data-test={`input-edit-highscore`}
      data-original-name={score.name}
    />
  );
}

export default HighScoreRename;
